// components/models/Ltx23FastControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import { Ltx23FastParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface Ltx23FastControlPanelProps {
  params: Ltx23FastParams;
  setModelParams: (params: (prevParams: { [model: string]: Ltx23FastParams }) => { [model: string]: Ltx23FastParams }) => void;
  diffusionModel: string;
}

const defaultLtx23FastParams: Ltx23FastParams = {
  prompt: '',
  duration: 6,
  resolution: '1080p',
  aspect_ratio: 'auto',
  fps: 25,
  generate_audio: false,
  seed: -1,
};

export const getDefaultLtx23FastParams = (): Ltx23FastParams => ({ ...defaultLtx23FastParams });

const RESOLUTION_OPTIONS: NonNullable<Ltx23FastParams['resolution']>[] = ['1080p', '1440p', '2160p'];
const ASPECT_RATIO_OPTIONS: NonNullable<Ltx23FastParams['aspect_ratio']>[] = ['auto', '16:9', '9:16'];
const FPS_OPTIONS = ['24', '25', '48', '50'];

const Ltx23FastControlPanel: React.FC<Ltx23FastControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Ltx23FastControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' ? { value_length: (newValue as string).length } : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'Ltx23FastControlPanel',
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Ltx23FastControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: name === 'fps' ? Number(value) : value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Ltx23FastControlPanel',
    });
  };

  const onSwitchGenerateAudio = () => {
    const newValue = !params.generate_audio;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Ltx23FastControlPanel] onSwitchGenerateAudio: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, generate_audio: newValue },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'generate_audio',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'Ltx23FastControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the motion you want from the current frame..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={6}
          max={20}
          step={2}
          value={params.duration}
          onChange={handleParamChange}
          description="Seconds, even values only."
        />
      </div>

      <div className="mb-4">
        <Select
          id="ltx-2-3-fast-resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={(value) => handleSelectChange('resolution', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="ltx-2-3-fast-aspect-ratio"
          label="Aspect Ratio"
          value={params.aspect_ratio ?? 'auto'}
          options={ASPECT_RATIO_OPTIONS}
          onChange={(value) => handleSelectChange('aspect_ratio', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="ltx-2-3-fast-fps"
          label="Frames Per Second"
          value={String(params.fps ?? 25)}
          options={FPS_OPTIONS}
          onChange={(value) => handleSelectChange('fps', value)}
        />
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.generate_audio === true}
          onChange={onSwitchGenerateAudio}
          labelRight="Generate Audio"
        />
      </div>
    </>
  );
});

Ltx23FastControlPanel.displayName = 'Ltx23FastControlPanel';

export default Ltx23FastControlPanel;
